/**
 * QuickFormModal - Minimal Form Modal Preset
 *
 * Lightweight preset for simple forms:
 * - Medium size (md)
 * - No character counter
 * - No dirty check
 * - No extra features
 * - Fast and minimal
 *
 * Ideal for:
 * - Login/Register forms
 * - Simple data entry
 * - Quick dialogs
 *
 * @module quick-form-modal
 */

'use client';

import * as React from 'react';

import { EnhancedFormModal, type EnhancedFormModalProps } from '../enhanced-form-modal';

/**
 * QuickFormModal props (extends core but with preset defaults)
 */
export type QuickFormModalProps = Omit<
  EnhancedFormModalProps,
  | 'showCharacterCount'
  | 'enableDirtyCheck'
  | 'enableKeyboardShortcuts'
  | 'autoFocusFirstField'
  | 'showProgress'
>;

/**
 * QuickFormModal - Minimal form modal preset
 *
 * Lightweight configuration for simple forms with minimal features.
 * Perfect for login forms, quick data entry, or simple dialogs.
 *
 * @example
 * ```tsx
 * <QuickFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Sign In"
 *   subtitle="Enter your credentials"
 * >
 *   <LoginForm />
 * </QuickFormModal>
 * ```
 */
export function QuickFormModal(props: QuickFormModalProps) {
  return (
    <EnhancedFormModal
      {...props}
      // Preset configuration
      size={props.size ?? 'md'}
      showCharacterCount={false}
      enableDirtyCheck={false}
      enableKeyboardShortcuts={true} // Keep this for accessibility
      autoFocusFirstField={true}
      showProgress={false}
    />
  );
}

QuickFormModal.displayName = 'QuickFormModal';
